import type { ColumnDefinition } from 'components/SubTable';
import type { Product } from 'payments-api/model';
import {
  useGetPartiesSuspense,
  useGetProductsSuspense,
  usePostInvoices,
} from 'api/payments/generated/fragmentPaymentsAPI';
import { MainViewLayout } from 'components/Layout/MainViewLayout';
import { SubTable } from 'components/SubTable';
import { useDocumentTitle } from 'hooks/useDocumentTitle';
import React, { Suspense, useCallback, useMemo, useState } from 'react';
import { makePath } from 'utils/routes';
import { useLocation } from 'wouter';
import { useQueryClient } from '@tanstack/react-query';

import type { CurrencyCode } from '@fragment/types';
import { PrimaryButtonV2 } from '@fragment/ui/src/components/Button/PrimaryButtonV2/PrimaryButtonV2';
import { TertiaryButton } from '@fragment/ui/src/components/Button/TertiaryButton/TertiaryButton';
import { TextInput } from '@fragment/ui/src/components/Input/TextInput';
import { Modal } from '@fragment/ui/src/components/Modal/Modal';
import { ModalDropdown } from '@fragment/ui/src/components/Modal/ModalDropdown';
import { ModalField } from '@fragment/ui/src/components/Modal/ModalField';
import { ModalTextInput } from '@fragment/ui/src/components/Modal/ModalTextInput';

type InvoiceItem = {
  tempId: string;
  productId: string;
  productCode: string;
  amount: string;
  currencyCode: CurrencyCode;
  payoutParty: string | null; // null when platform payout
  isPlatformPayout: boolean;
  description: string;
};

// Helper to get seller type display text from Product
const getSellerTypeDisplay = (product: Product): string => {
  if (product.seller.soldByPlatform) {
    return 'Platform';
  }
  // For CounterpartySeller, access counterpartyType
  return 'counterpartyType' in product.seller
    ? product.seller.counterpartyType
    : 'Counterparty';
};

type ProductWithId = Product & { id: string };

// Define schema for products table
const productsSchema: readonly ColumnDefinition<ProductWithId>[] = [
  {
    title: 'Code',
    format: (item: ProductWithId) => item.code,
  },
  {
    title: 'Description',
    format: (item: ProductWithId) => (
      <span className="text-sm">{item.description}</span>
    ),
  },
  {
    title: 'Seller',
    justify: 'right' as const,
    format: (item: ProductWithId) => getSellerTypeDisplay(item),
  },
];

type Props = {
  workspaceId: string;
};

const CreateInvoicePageContent = ({ workspaceId }: Props): JSX.Element => {
  useDocumentTitle('Create Invoice');

  const queryClient = useQueryClient();
  const { data: productsData } = useGetProductsSuspense();
  const { data: partiesData } = useGetPartiesSuspense();
  const createInvoiceMutation = usePostInvoices();
  const [invoiceItems, setInvoiceItems] = useState<InvoiceItem[]>([]);
  const [ik, setIk] = useState('');
  const [buyerParty, setBuyerParty] = useState('');
  const [, setLocation] = useLocation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [modalPayoutParty, setModalPayoutParty] = useState('');
  const [modalDescription, setModalDescription] = useState('');
  const [modalAmount, setModalAmount] = useState('');
  const [modalCurrencyCode, setModalCurrencyCode] = useState('USD');

  const parties = partiesData.data;
  const rawProducts = productsData.data;

  // Add id field to products for SubTable compatibility (uses code as id)
  const products: ProductWithId[] = useMemo(
    () =>
      rawProducts.map((product) => ({
        ...product,
        id: product.code,
      })),
    [rawProducts]
  );

  // Generate a temporary invoice ID for preview
  const currentTimestamp = useMemo(() => new Date().toISOString(), []);

  const handleProductClick = useCallback((item: ProductWithId) => {
    setSelectedProduct(item);
    setModalPayoutParty('');
    setModalDescription(item.description);
    setModalAmount('');
    setModalCurrencyCode('USD');
    setIsModalOpen(true);
  }, []);

  // Convert all parties to dropdown options
  const partyOptions = useMemo(
    () =>
      parties.map((party) => ({
        value: party.externalId,
        label: party.externalId,
      })),
    [parties]
  );

  const handleModalClose = useCallback(() => {
    setIsModalOpen(false);
    setSelectedProduct(null);
    setModalPayoutParty('');
    setModalDescription('');
    setModalAmount('');
    setModalCurrencyCode('USD');
  }, []);

  const handleAddInvoiceItem = useCallback(() => {
    if (!selectedProduct || !modalDescription || !modalAmount) return;

    const isPlatformPayout = selectedProduct.seller.soldByPlatform;

    // For counter-party products, payout party must be selected
    if (!isPlatformPayout && !modalPayoutParty) return;

    const newItem: InvoiceItem = {
      tempId: Date.now().toString(),
      productId: selectedProduct.code, // Use code as the product identifier
      productCode: selectedProduct.code,
      amount: modalAmount,
      currencyCode: modalCurrencyCode as CurrencyCode,
      payoutParty: isPlatformPayout ? null : modalPayoutParty,
      isPlatformPayout,
      description: modalDescription,
    };

    setInvoiceItems((prev) => [...prev, newItem]);
    handleModalClose();
  }, [
    selectedProduct,
    modalPayoutParty,
    modalDescription,
    modalAmount,
    modalCurrencyCode,
    handleModalClose,
  ]);

  const removeInvoiceItem = useCallback((tempId: string) => {
    setInvoiceItems((prev) => prev.filter((item) => item.tempId !== tempId));
  }, []);

  const handleCreateInvoice = useCallback(async () => {
    try {
      // Map invoice items to API format with snake_case keys
      const lineItems = invoiceItems.map((item) => ({
        payout_party: item.isPlatformPayout
          ? { platform: true as const }
          : { party_id: item.payoutParty as string },
        product_id: item.productId,
        amount: item.amount,
        currency_code: item.currencyCode,
        description: item.description,
      }));

      await createInvoiceMutation.mutateAsync({
        data: {
          ik,
          buyerParty,
          lineItems: lineItems.length > 0 ? lineItems : undefined,
        },
      });

      await queryClient.invalidateQueries({ queryKey: ['/invoices'] });

      // Redirect to invoices list page
      setLocation(
        makePath(['w', workspaceId, 'payments', 'invoices'], { absolute: true })
      );
    } catch (error) {
      console.error('Error creating invoice:', error);
      // TODO: Show error to user
    }
  }, [
    createInvoiceMutation,
    ik,
    buyerParty,
    invoiceItems,
    workspaceId,
    setLocation,
    queryClient,
  ]);

  const handleBackToInvoices = useCallback(() => {
    setLocation(
      makePath(['w', workspaceId, 'payments', 'invoices'], { absolute: true })
    );
  }, [workspaceId, setLocation]);

  const totalAmount = useMemo(
    () => invoiceItems.reduce((sum, item) => sum + Number(item.amount), 0),
    [invoiceItems]
  );

  return (
    <>
      <MainViewLayout
        subHeader={
          <div className="h-f8 pt-f1">
            <button
              type="button"
              onClick={handleBackToInvoices}
              className="text-main-500 hover:text-main flex items-center gap-f1 text-medium"
            >
              ← Invoices
            </button>
          </div>
        }
        hideSearch
        mode="payments"
      >
        <div className="flex flex-col gap-f3 w-full h-full pt-f4">
          {/* Action Buttons */}

          <div className="flex flex-row justify-between items-start">
            <div className="flex flex-col gap-f3">
              <div className="flex flex-col gap-f2">
                <label htmlFor="invoice-ik" className="font-medium text-medium">
                  Invoice Key
                </label>
                <TextInput
                  id="invoice-ik"
                  value={ik}
                  onChange={(e) => setIk(e.target.value)}
                  placeholder="Enter invoice idempotency key"
                />
              </div>
              <div className="flex flex-col gap-f2">
                <label
                  htmlFor="buyer-party"
                  className="font-medium text-medium"
                >
                  Buyer Party
                </label>
                <ModalDropdown
                  options={partyOptions.map((party) => party.value)}
                  value={buyerParty}
                  onChange={setBuyerParty}
                  placeholder="Select buyer party"
                  data-testid="buyer-party-dropdown"
                />
              </div>
            </div>
            <div className="flex justify-end gap-f2">
              <TertiaryButton onClick={handleBackToInvoices}>
                Cancel
              </TertiaryButton>
              <button
                type="button"
                onClick={handleCreateInvoice}
                disabled={createInvoiceMutation.isPending || !ik || !buyerParty}
                className="flex items-center h-f4 px-fone-half text-md bg-main text-negative hover:bg-negative hover:text-main hover:cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {createInvoiceMutation.isPending
                  ? 'Creating...'
                  : 'Create Invoice'}
              </button>
            </div>
          </div>

          {/* Two-column layout */}
          <div className="grid grid-cols-2 gap-f4 w-full h-full">
            {/* Left Column - Products */}
            <div className="flex flex-col gap-f3">
              <div className="flex flex-col gap-f2">
                <h3>Products</h3>
                <div>
                  <SubTable
                    data={products}
                    schema={productsSchema}
                    onClick={handleProductClick}
                    textSize="md"
                    showHeader
                    stickyHeader={false}
                    emptyValue={{
                      Code: (
                        <span className="text-main-500">
                          No products available
                        </span>
                      ),
                      Description: '',
                      Seller: '',
                    }}
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Invoice Preview */}
            <div className="flex flex-col gap-f2 pl-f1 border-l-[0.5px] border-main-300">
              <div className="">
                <h2 className="text-lg mb-f3 text-medium">Preview</h2>
                <div className="flex flex-col gap-f2 mb-f3">
                  <div className="text-medium">
                    <span className="text-main-500">Invoice Key: </span>
                    <span className="font-mono">{ik || '(not set)'}</span>
                  </div>
                  <div className="text-medium">
                    <span className="text-main-500">Buyer Party: </span>
                    <span className="font-mono">
                      {buyerParty || '(not set)'}
                    </span>
                  </div>
                  <div className="text-medium">
                    <span className="text-main-500">Created: </span>
                    <span>{new Date(currentTimestamp).toLocaleString()}</span>
                  </div>
                  <div className="text-medium">
                    <span className="text-main-500">Status: </span>
                    <span className="font-medium">draft</span>
                  </div>
                </div>

                {/* Invoice Items */}
                <div className="border-t border-main-300 pt-f3">
                  <h3 className="text-medium mb-f2">Line Items</h3>
                  {invoiceItems.length === 0 ? (
                    <div className="text-main-500 text-medium">
                      No items added yet
                    </div>
                  ) : (
                    <div className="flex flex-col gap-f2">
                      {invoiceItems.map((item) => (
                        <div
                          key={item.tempId}
                          className="flex items-center justify-between p-f2 border border-gray-200 rounded"
                        >
                          <div className="flex flex-col gap-f1 flex-1">
                            <div className="text-medium font-semibold">
                              {item.productCode}
                            </div>
                            <div className="text-sm text-main-500">
                              {item.description}
                            </div>
                            <div className="text-sm text-main-500">
                              To:{' '}
                              {item.isPlatformPayout
                                ? 'Platform'
                                : item.payoutParty}
                            </div>
                          </div>
                          <div className="flex items-center gap-f2">
                            <span className="text-medium font-semibold">
                              {item.currencyCode}{' '}
                              {(Number(item.amount) / 100).toFixed(2)}
                            </span>
                            <button
                              type="button"
                              onClick={() => removeInvoiceItem(item.tempId)}
                              className="text-main-500 hover:text-red-600"
                            >
                              ✕
                            </button>
                          </div>
                        </div>
                      ))}
                      <div className="flex justify-between items-center pt-f2 border-t border-gray-300">
                        <span className="text-medium font-semibold">
                          Total:
                        </span>
                        <span className="text-medium font-bold">
                          ${(totalAmount / 100).toFixed(2)}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </MainViewLayout>

      {/* Modal for adding line item */}
      {isModalOpen && selectedProduct && (
        <Modal
          onClose={handleModalClose}
          header={`Add ${selectedProduct.code} to Invoice`}
          onSubmit={(e) => {
            e.preventDefault();
            handleAddInvoiceItem();
          }}
          body={
            <div className="flex flex-col space-y-f2">
              <ModalField
                label={<label htmlFor="payoutParty">Payout Party</label>}
              >
                {selectedProduct.seller.soldByPlatform ? (
                  <div className="text-medium text-main-500 py-f1">
                    Platform (automatic for platform-sold products)
                  </div>
                ) : (
                  <ModalDropdown
                    options={partyOptions.map((party) => party.value)}
                    value={modalPayoutParty}
                    onChange={setModalPayoutParty}
                    placeholder="Select a party"
                    data-testid="payout-party-dropdown"
                  />
                )}
              </ModalField>
              <ModalField
                label={<label htmlFor="amount">Amount (cents)</label>}
              >
                <ModalTextInput
                  id="amount"
                  type="number"
                  value={modalAmount}
                  onChange={(e) => setModalAmount(e.target.value)}
                  placeholder="Enter amount in cents"
                  data-testid="amount-input"
                />
              </ModalField>
              <ModalField
                label={<label htmlFor="currencyCode">Currency</label>}
              >
                <ModalDropdown
                  options={['USD', 'EUR', 'GBP', 'BTC', 'ETH', 'USDC']}
                  value={modalCurrencyCode}
                  onChange={setModalCurrencyCode}
                  placeholder="Select currency"
                  data-testid="currency-dropdown"
                />
              </ModalField>
              <ModalField
                label={<label htmlFor="description">Description</label>}
              >
                <ModalTextInput
                  id="description"
                  type="text"
                  value={modalDescription}
                  onChange={(e) => setModalDescription(e.target.value)}
                  placeholder="Enter a description"
                  data-testid="description-input"
                />
              </ModalField>
            </div>
          }
          footer={
            <div className="flex justify-end gap-f2">
              <TertiaryButton onClick={handleModalClose}>Cancel</TertiaryButton>
              <PrimaryButtonV2 onClick={handleAddInvoiceItem}>
                Add Item
              </PrimaryButtonV2>
            </div>
          }
        />
      )}
    </>
  );
};

export const CreateInvoicePage = ({ workspaceId }: Props): JSX.Element => (
  <Suspense
    fallback={
      <MainViewLayout hideSearch>
        <div className="text-main-500 text-medium">Loading...</div>
      </MainViewLayout>
    }
  >
    <CreateInvoicePageContent workspaceId={workspaceId} />
  </Suspense>
);
